/**
 * Safe URL helpers for respondent-supplied links (S7).
 *
 * Respondent-provided values (LinkedIn profile, company URL) are rendered
 * as anchor hrefs inside the authenticated admin session. A stored
 * `javascript:` (or `data:`, `vbscript:`, ...) URL would execute when an
 * admin clicks the link, so only absolute http/https URLs may be stored
 * or rendered. Shared by the create-response endpoint, the interview
 * service, and the admin render layer.
 */

/** URL protocols allowed for untrusted, user-supplied links. */
const SAFE_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Render guard for untrusted hrefs.
 *
 * Uses the real WHATWG URL parser (which strips embedded tabs/newlines
 * and lowercases the scheme) so obfuscated payloads like `java\tscript:`
 * cannot slip past a string check.
 *
 * @param value - Untrusted URL string, possibly null/undefined.
 * @returns The trimmed value when it parses as an absolute http/https URL, otherwise null.
 */
export function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  return SAFE_PROTOCOLS.has(parsed.protocol) ? trimmed : null;
}

/**
 * Normalizes untrusted URL input before storage.
 *
 * Scheme-less values (e.g. "linkedin.com/in/jane") get an `https://`
 * prefix so respondents are not forced to type a scheme; values that
 * already carry a non-http(s) scheme are dangerous and return null,
 * which callers must reject.
 *
 * @param value - Raw user-supplied URL string.
 * @returns A valid absolute http/https URL string, or null when the input cannot be made safe.
 */
export function normalizeHttpUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const direct = safeHttpUrl(trimmed);
  if (direct) return direct;
  try {
    // Parses with a scheme we do not allow (javascript:, data:, ...).
    new URL(trimmed);
    return null;
  } catch {
    // No scheme at all: retry with an https:// prefix.
    return safeHttpUrl(`https://${trimmed}`);
  }
}
